import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../components/ui.js';

export function MenuBoardsScreen(): ReactNode {
  const boards = useQuery(() => api.menuBoards(), []);
  const navigate = useNavigate();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function create(): Promise<void> {
    setCreating(true);
    try {
      const board = await api.createMenuBoard('Untitled menu');
      navigate(`/menu-boards/${board.id}`);
    } catch (err) {
      toast.error(err);
    } finally {
      setCreating(false);
    }
  }

  async function remove(id: string, name: string): Promise<void> {
    if (!confirm(`Delete menu board “${name}”?`)) return;
    setBusyId(id);
    try {
      await api.deleteMenuBoard(id);
      boards.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Page
      title="Menu boards"
      actions={
        <button className="btn primary" disabled={creating} onClick={() => void create()}>
          {creating ? 'Creating…' : 'New menu board'}
        </button>
      }
    >
      {boards.loading && !boards.data ? <Loading what="menu boards" /> : null}
      {boards.error ? <ErrorNote error={boards.error} /> : null}

      {boards.data ? (
        boards.data.length === 0 ? (
          <EmptyState>
            No menu boards yet. Create one, then add it to a layout with the “menuboard” widget.
          </EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Currency</th>
                  <th>Categories</th>
                  <th>Products</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {boards.data.map((b) => (
                  <tr key={b.id} className="clickable" onClick={() => navigate(`/menu-boards/${b.id}`)}>
                    <td>{b.name}</td>
                    <td className="muted small">{b.currency}</td>
                    <td className="muted small">{b.categoryCount}</td>
                    <td className="muted small">{b.productCount}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn small danger"
                        disabled={busyId === b.id}
                        onClick={() => void remove(b.id, b.name)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Page>
  );
}
